/**
 * MobileNav — the bottom bar every phone user navigates with.
 *
 * RW-2378: this file used to build itself from two hardcoded module lists
 * (`TAB_ITEMS`, `DRAWER_ITEMS_BASE`) with no connection to src/lib/navigation.js,
 * which has been the single source of truth since RW-1782. The result: of the
 * 12 top-level sections, the bar named exactly two — and it still offered
 * "Training", a module that no longer exists. Nothing was unreachable (the
 * Sidebar drawer renders on mobile too and was on the new tree), but mobile
 * had TWO nav surfaces with independently-maintained contents, so anything
 * added to navigation.js appeared in one and not the other, silently. That is
 * the paired-registry class — the same shape as RW-1782, RW-2155 and RW-2329.
 *
 * Everything here is now derived from navigation.js through the SAME
 * visibleNavSections() the Sidebar rail uses, so the two cannot disagree.
 * src/components/MobileNav.navParity.test.js fails if a hardcoded list ever
 * comes back.
 *
 * The four bottom tabs are chosen by an explicit `mobileTab` field on the
 * section/row rather than a second list, and are ordered BY THAT NUMBER —
 * never by position in the tree, because STAFF_SECTIONS reorders the same
 * sections and ordering by position would hand staff a different bottom bar.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { colors, fonts, radius } from './theme';
import NavIcon from './NavIcon';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { isFeatureEnabled } from '../lib/featureFlags';
import { isWindDownAllowedPath } from '../lib/windDown';
import { isStaffSelf } from '../lib/permissions';
import {
  SECTIONS, STAFF_SECTIONS, FOOTER_ITEMS, STAFF_FOOTER_ITEMS,
  navRoleAllows, pathActive, visibleNavSections, mobileTabRows,
} from '../lib/navigation';

export default function MobileNav({
  user, isAdmin = false, isTrainer = false, isStaff = false, windingDown = false,
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { flags } = useFeatureFlags();
  const pathname = location.pathname;

  // Close the slide-up drawer on Escape (the overlay div can't hold focus,
  // so a window-level listener is the reliable keyboard path).
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // Close the drawer whenever the route changes — including a back gesture,
  // which does not go through handleDrawerNav.
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  /* ── Gating, identical to Sidebar's (RW-1787 staff variant, RW-1568 role
     gate, RW-899 wind-down allowlist). Same inputs, same helpers — a
     destination this bar offers is one the rail would also render. ── */
  const staffSelf = useMemo(() => isStaffSelf(user) && !isAdmin, [user, isAdmin]);
  const dealRole = user?.role || '';
  const staffVariant = isAdmin || dealRole === 'tc' || dealRole === 'broker';
  const roleAllows = useCallback(
    (item) => navRoleAllows(item, { isAdmin, isTrainer, isStaff, staffSelf, dealRole }),
    [isAdmin, isTrainer, isStaff, staffSelf, dealRole],
  );

  const sections = useMemo(() =>
    visibleNavSections(staffVariant ? STAFF_SECTIONS : SECTIONS, {
      roleAllows,
      isFlagEnabled: (key) => isFeatureEnabled(key, flags, isAdmin),
      isPathAllowed: (path) => !windingDown || isWindDownAllowedPath(path),
      // RW-2581 rail curation, with the admin bypass since RW-2624 (Admin
      // view shows every section; "Viewing as Agent" shows the curated
      // rail). Must match Sidebar's predicate exactly — the bottom bar is
      // derived from these sections, so any difference makes the phone and
      // the desktop rail disagree (the RW-2378 drift class).
      isRailEnabled: (key) => isFeatureEnabled(key, flags, isAdmin),
    }),
    [flags, isAdmin, windingDown, roleAllows, staffVariant],
  );

  const tabs = useMemo(() => mobileTabRows(sections), [sections]);
  const tabPaths = useMemo(() => new Set(tabs.map((t) => t.path)), [tabs]);

  /* ── The drawer is EVERYTHING ELSE, grouped by its section, so a section
     that is not one of the four tabs is still one tap away. Previously eight
     of twelve sections appeared here not at all. ── */
  const drawerGroups = useMemo(() => {
    const footer = (staffVariant ? STAFF_FOOTER_ITEMS : FOOTER_ITEMS).filter(
      (it) => roleAllows(it)
        && isFeatureEnabled(it.flagKey, flags, isAdmin)
        && (!windingDown || isWindDownAllowedPath(it.path))
        && it.path,
    );
    const groups = sections.map((s) => {
      if (s.standalone) {
        // A standalone section that is already a tab needs no drawer row.
        return tabPaths.has(s.path) ? null : { label: s.label, items: [s] };
      }
      const items = (s.items || []).filter((it) => it.path && !it.noActive && !tabPaths.has(it.path));
      return items.length ? { label: s.label, items } : null;
    }).filter(Boolean);
    return footer.length ? [...groups, { label: 'More', items: footer }] : groups;
  }, [sections, tabPaths, staffVariant, roleAllows, flags, isAdmin, windingDown]);

  const isTabActive = useCallback(
    (tab) => (tab.matchPaths || [tab.path]).some((p) => pathActive(p, pathname)),
    [pathname],
  );
  // RW-3578: More lights up for a page that lives in its drawer — not for
  // every page no tab claims. Search (and any other page with no home in the
  // bar or the drawer) used to light More, though More doesn't lead there.
  const inDrawer = drawerGroups.some((g) => g.items.some((it) => (it.matchPaths || [it.path]).some((p) => pathActive(p, pathname))));
  const moreIsActive = drawerOpen || (!tabs.some(isTabActive) && inDrawer);

  const go = (path) => { navigate(path); setDrawerOpen(false); };

  return (
    <>
      {/* Overlay */}
      {drawerOpen ? (
        <div
          role="button"
          tabIndex={-1}
          aria-label="Close menu"
          onClick={() => setDrawerOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setDrawerOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 58 }}
        />
      ) : null}

      {/* Slide-up Drawer */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 'calc(64px + var(--reve-safe-bottom, 0px))',
          zIndex: 59,
          background: colors.white,
          borderRadius: '12px 12px 0 0',
          maxHeight: '60vh',
          overflowY: 'auto',
          padding: '24px 20px 20px',
          boxShadow: '0 -4px 24px rgba(15,16,17,0.18)',
          transform: drawerOpen ? 'translateY(0)' : 'translateY(110%)',
          transition: 'transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)',
          pointerEvents: drawerOpen ? 'auto' : 'none',
        }}
        aria-hidden={!drawerOpen}
      >
        {/* Drag handle */}
        <div style={{ width: 40, height: 4, background: colors.pearl, borderRadius: 2, margin: '0 auto 20px' }} />
        {drawerGroups.map((group) => (
          // RW-2378: a named group, so each section's rows are addressable as
          // a unit by assistive tech (and by the parity test) rather than
          // being one flat run of buttons under a decorative heading.
          <div key={group.label} role="group" aria-label={group.label}>
            <p
              style={{
                fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
                color: colors.gray1, fontFamily: fonts.body, margin: '0 0 8px 4px',
              }}
            >
              {group.label}
            </p>
            {group.items.map((item) => {
              const active = pathActive(item.path, pathname);
              return (
                <button
                  key={item.key || item.id}
                  type="button"
                  onClick={() => go(item.path)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    padding: '14px 16px', marginBottom: 4,
                    background: active ? 'rgba(238,129,114,0.1)' : 'transparent',
                    border: 'none', borderRadius: radius.md, cursor: 'pointer',
                    fontFamily: fonts.body, fontSize: 16,
                    fontWeight: active ? 600 : 400,
                    color: active ? colors.coralInk : colors.noir,
                    textAlign: 'left',
                  }}
                >
                  {item.icon ? <NavIcon name={item.icon} size={17} /> : null}
                  {item.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Bottom Tab Bar

          RW-2887: border-box, so the safe-area padding lives INSIDE the
          height. There is no global box-sizing rule, and as a content box the
          bar stood 64px + TWO home-indicator insets tall on an iPhone (~132pt
          instead of ~98pt). Everything stacked above it — FLOW's tab bar, the
          More drawer — sits at `64px + safe-area` and was buried 34pt under it. */}
      <nav
        aria-label="Mobile navigation"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          boxSizing: 'border-box',
          height: 'calc(64px + var(--reve-safe-bottom, 0px))',
          paddingBottom: 'var(--reve-safe-bottom, 0px)',
          background: colors.noir, display: 'flex', zIndex: 60,
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {[...tabs.map((t) => ({ ...t, isMore: false })), { key: '__more', label: 'More', icon: 'sliders', isMore: true }]
          .map((tab) => {
            const isActive = tab.isMore ? moreIsActive : isTabActive(tab);
            return (
              <button
                key={tab.key}
                type="button"
                aria-label={tab.label}
                aria-current={isActive && !tab.isMore ? 'page' : undefined}
                aria-expanded={tab.isMore ? drawerOpen : undefined}
                onClick={() => (tab.isMore ? setDrawerOpen((o) => !o) : go(tab.path))}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: 3, background: 'transparent',
                  border: 'none', cursor: 'pointer', padding: 0,
                }}
              >
                <NavIcon name={tab.icon} size={20} color={isActive ? colors.coral : colors.white} />
                <span
                  style={{
                    fontSize: 10, fontFamily: fonts.body,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? colors.coral : colors.white,
                    transition: 'color 0.15s', letterSpacing: '0.02em',
                  }}
                >
                  {tab.label}
                </span>
              </button>
            );
          })}
      </nav>
    </>
  );
}
